/* ==========================================================================
   PULSEMED — All-in-one JS (Impact 4.x)
   CORE + Cart CGV gate + Modal délai/devis
   Fichier: assets/pulsemed.js
   ========================================================================== */


(() => {
  'use strict';

  const PM = (window.PULSEMED = window.PULSEMED || {});
  PM.DEBUG = PM.DEBUG ?? false;

  // Anti double init global (si le fichier est chargé 2x)
  if (PM.__initialized) return;
  PM.__initialized = true;

  const log = (...a) => PM.DEBUG && console.log('[PULSEMED]', ...a);

  const once = (key, fn) => {
    PM.__once = PM.__once || new Set();
    if (PM.__once.has(key)) return;
    PM.__once.add(key);
    fn();
  };

  const qs = (sel, el = document) => el.querySelector(sel);
  const qsa = (sel, el = document) => Array.from(el.querySelectorAll(sel));

  const isCart = () => {
    const p = location.pathname.replace(/\/+$/, '');
    return p === '/cart' || p.endsWith('/cart');
  };

  /**
   * Chemin page panier avec préfixe langue Shopify (ex. /cart, /fr/cart).
   * Utilisé après ajout panier bundle — évite une redirection vers checkout / mauvaise locale.
   */
  function getShopifyCartPagePath() {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    const prefix = root === '/' ? '' : String(root).replace(/\/$/, '');
    return `${prefix}/cart`;
  }

  /** Préfixe API panier (même logique que theme.js) — obligatoire pour conserver la langue (DE/IT/FR). */
  function shopifyRoutesRoot() {
    const r = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    return r.endsWith('/') ? r : (r === '/' ? '/' : `${r}/`);
  }

  /* =====================================================
     CORE (Impact) — tes features existantes
     ===================================================== */

  function initTabsCustom() {
    qsa('.tab-links').forEach((tabsWrapper) => {
      if (tabsWrapper.dataset.pulsemedInit === '1') return;
      tabsWrapper.dataset.pulsemedInit = '1';

      const tabs = qsa('li', tabsWrapper);
      const contents = tabsWrapper.closest('.tabs-container')?.querySelectorAll('.tab-content');
      if (!contents || !contents.length) return;

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          tabs.forEach(t => t.classList.remove('active'));
          contents.forEach(c => c.classList.remove('active'));
          tab.classList.add('active');
          contents[index]?.classList.add('active');
        });
      });
    });
  }

  function applyVariantsOpenFlag() {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('variants_open') === '1') {
        const acc = qs('.product-variants-accordion');
        if (acc) acc.setAttribute('open', 'open');
      }
    } catch (err) {
      console.warn('[PULSEMED] variants_open error', err);
    }
  }

  function initBlockImpactVariantAdd() {
    once('blockVariantAdd', () => {
      document.addEventListener('variant:add', (e) => {
        if (e?.detail?.source === 'pulsemed') e.stopImmediatePropagation();
      }, true);
    });
  }

  function initBundleAddToCart() {
    once('bundleAddToCart', () => {
      document.addEventListener('submit', (event) => {
        const form = event.target?.closest?.('form');
        if (!form) return;

        const action = (form.getAttribute('action') || '').toLowerCase();
        if (!action.includes('/cart/add')) return;

        const idInput = qs('input[name="id"]', form);
        if (!idInput) return;

        event.preventDefault();
        event.stopImmediatePropagation?.();

        const formData = new FormData(form);
        let mainQty = parseInt(formData.get('quantity'), 10);
        if (Number.isNaN(mainQty) || mainQty < 1) mainQty = 1;

        const qtyInput = qs('input[name="quantity"]', form);
        if (qtyInput) {
          const min = parseInt(qtyInput.getAttribute('min') || '1', 10);
          const rawMaxAttr = qtyInput.getAttribute('max');
          const rawMax = parseInt(rawMaxAttr || '', 10);
          const hasMaxAttr = rawMaxAttr !== null && rawMaxAttr !== '';
          const hasMax = Number.isFinite(rawMax);
          const safeMin = Number.isFinite(min) && min > 0 ? min : 1;
          const safeMax = hasMax ? rawMax : Number.MAX_SAFE_INTEGER;
          if (hasMaxAttr && safeMax <= 0) {
            return;
          }
          mainQty = Math.max(safeMin, Math.min(mainQty, safeMax));
        }

        let mainVariantId = null;

        const checked = qs('input[name="id"]:checked', form) || qs('input[name="id"]:checked', document);
        if (checked) mainVariantId = parseInt(checked.value, 10);

        if (!mainVariantId) {
          const hidden = qs('input[name="id"]', form);
          if (hidden) mainVariantId = parseInt(hidden.value, 10);
        }

        if (!mainVariantId || Number.isNaN(mainVariantId)) {
          form.submit();
          return;
        }

        const items = [{ id: mainVariantId, quantity: mainQty }];

        const productRoot = form.closest('[data-section-type*="product"], .product, .shopify-section') || document;
        qsa('.accessory-checkbox:checked', productRoot).forEach((checkbox) => {
          const variantId = parseInt(checkbox.dataset.variantId || checkbox.value, 10);
          if (!variantId || variantId === mainVariantId) return;
          items.push({ id: variantId, quantity: 1 });
        });

        /** Attributs panier (ex. fil d’Ariane collection) — champs attributes[clé] du formulaire */
        const cartAttributes = {};
        qsa('input[name^="attributes["]', form).forEach((input) => {
          const raw = input.getAttribute('name') || '';
          const m = /^attributes\[(.+)\]$/.exec(raw);
          if (m && input.value) cartAttributes[m[1]] = input.value;
        });

        const payload = { items };
        if (Object.keys(cartAttributes).length) payload.attributes = cartAttributes;

        fetch(`${shopifyRoutesRoot()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        })
          .then(() => {
            document.dispatchEvent(new CustomEvent('variant:add', { detail: { source: 'pulsemed' } }));
            const cartPage = getShopifyCartPagePath();
            window.location.href = cartPage;
          })
          .catch(() => {
            window.location.href = getShopifyCartPagePath();
          });
      }, true);
    });
  }

  function initCartQtyBridge() {
    once('cartQtyBridge', () => {
      document.addEventListener('click', (event) => {
        const button = event.target.closest('.cart-item__quantity-selector .quantity-selector__button');
        if (!button) return;

        const input = button.closest('.cart-item__quantity-selector')?.querySelector('input.quantity-input');
        if (!input) return;

        setTimeout(() => {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }, 0);
      });
    });
  }

  function initQuantityLimitsGuard() {
    once('quantityLimitsGuard', () => {
      const isStrictOutOfStock = (variant) => {
        return !!(
          variant &&
          variant.inventory_management &&
          variant.inventory_policy === 'deny' &&
          parseInt(variant.inventory_quantity || 0, 10) <= 0
        );
      };

      const readBound = (input, attr, fallback) => {
        const raw = input.getAttribute(attr);
        const parsed = parseInt(raw || '', 10);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const syncProductSubmitButtons = (input) => {
        const form = input?.form;
        if (!form) return;
        const submitButtons = qsa('button[type="submit"]', form);
        if (!submitButtons.length) return;
        const min = readBound(input, 'min', 1);
        const max = readBound(input, 'max', Number.MAX_SAFE_INTEGER);
        const value = parseInt(input.value || `${min}`, 10) || min;
        const blocked = max <= 0 || value < min || value > max;
        submitButtons.forEach((btn) => {
          btn.disabled = blocked;
          btn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
        });
        form.dataset.pmStrictOos = blocked ? '1' : '';
      };

      const syncSelectorState = (selector) => {
        if (!selector) return;
        const input = qs('input[is="quantity-input"], input.quantity-input', selector);
        if (!input) return;

        const min = readBound(input, 'min', 1);
        const max = readBound(input, 'max', Number.MAX_SAFE_INTEGER);
        const value = parseInt(input.value || '1', 10) || min;

        const minusBtn = qs('button:first-of-type', selector);
        const plusBtn = qs('button:last-of-type', selector);
        const locked = max <= min;

        if (minusBtn) minusBtn.disabled = locked || value <= min;
        if (plusBtn) plusBtn.disabled = locked || value >= max;

        input.readOnly = locked;
        input.setAttribute('aria-disabled', locked ? 'true' : 'false');
        syncProductSubmitButtons(input);
      };

      const clampInputValue = (input) => {
        if (!input) return;
        const min = readBound(input, 'min', 1);
        const max = readBound(input, 'max', Number.MAX_SAFE_INTEGER);
        let value = parseInt(input.value || `${min}`, 10);

        if (!Number.isFinite(value)) value = min;
        value = Math.max(min, Math.min(value, max));

        if (String(value) !== input.value) {
          input.value = String(value);
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      };

      const syncAll = () => {
        qsa('quantity-selector').forEach(syncSelectorState);
        qsa('line-item input.quantity-input[data-line-key]').forEach((input) => {
          clampInputValue(input);
          const max = readBound(input, 'max', Number.MAX_SAFE_INTEGER);
          const min = readBound(input, 'min', 1);
          const locked = max <= min;
          input.readOnly = locked;
          input.setAttribute('aria-disabled', locked ? 'true' : 'false');
        });
        qsa('form[is="product-form"] input[name="quantity"]').forEach((input) => {
          clampInputValue(input);
          syncSelectorState(input.closest('quantity-selector'));
          syncProductSubmitButtons(input);
        });
      };

      document.addEventListener('input', (e) => {
        const input = e.target.closest('input[is="quantity-input"], input.quantity-input');
        if (!input) return;
        clampInputValue(input);
        syncSelectorState(input.closest('quantity-selector'));
      }, true);

      document.addEventListener('change', (e) => {
        const input = e.target.closest('input[is="quantity-input"], input.quantity-input');
        if (!input) return;
        clampInputValue(input);
        syncSelectorState(input.closest('quantity-selector'));
      }, true);

      document.addEventListener('click', (e) => {
        const btn = e.target.closest('quantity-selector .quantity-selector__button');
        if (!btn) return;
        setTimeout(syncAll, 0);
      }, true);

      document.addEventListener('variant:change', (e) => {
        const form = e.target instanceof HTMLFormElement ? e.target : e.target?.closest?.('form[is="product-form"]');
        if (!form) return;

        const input = qs('input[name="quantity"][is="quantity-input"], input[name="quantity"].quantity-input', form);
        const variant = e.detail?.variant;
        const shouldLimit = !!(variant && variant.inventory_management && variant.inventory_policy === 'deny');
        const maxQty = shouldLimit ? Math.max(0, parseInt(variant.inventory_quantity || 0, 10)) : null;
        const strictOos = isStrictOutOfStock(variant);

        // Always force submit buttons in strict OOS mode, even without quantity selector block.
        qsa('button[type="submit"]', form).forEach((btn) => {
          btn.disabled = strictOos;
          btn.setAttribute('aria-disabled', strictOos ? 'true' : 'false');
        });
        form.dataset.pmStrictOos = strictOos ? '1' : '';

        if (!input) return;

        if (maxQty !== null) {
          input.setAttribute('max', String(maxQty));
        } else {
          input.removeAttribute('max');
        }

        // strict anti-oversell only applies when policy is deny
        if (maxQty !== null && maxQty <= 0) {
          input.value = '1';
        }

        clampInputValue(input);
        syncSelectorState(input.closest('quantity-selector'));
        syncProductSubmitButtons(input);
      }, true);

      // Hard submit guard: never submit product form if strict out-of-stock.
      document.addEventListener('submit', (e) => {
        const form = e.target?.closest?.('form[is="product-form"]');
        if (!form) return;
        if (form.dataset.pmStrictOos === '1') {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      document.documentElement.addEventListener('cart:change', syncAll);
      syncAll();
    });
  }

  function initCustomVariantReload() {
    once('customVariantReload', () => {
      document.addEventListener('change', (e) => {
        const input = e.target.closest('input[name="variant_selector_custom"]');
        if (!input) return;

        const variantId = input.dataset.variantId;
        if (!variantId) return;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        url.searchParams.set('variants_open', '1');
        window.location.href = url.toString();
      }, true);
    });
  }

  /* =====================================================
     CART — CGV gate
     ===================================================== */
  function pmCartCheckoutForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    const attr = form.getAttribute('action');
    const action = (attr && attr.trim() ? attr : form.action || window.location.pathname || '').toLowerCase();
    if (!action.includes('/cart') || action.includes('/cart/add')) return false;
    return (
      form.classList.contains('cart-form') ||
      form.classList.contains('shopify-cart-form') ||
      form.querySelector('button[name="checkout"], input[name="checkout"]')
    );
  }

  /** Formulaire « langue » (boutons locale_code), pas le sélecteur pays seul. */
  function pmFindLanguageLocalizationForm() {
    const forms = document.querySelectorAll('form.shopify-localization-form');
    for (let i = 0; i < forms.length; i++) {
      if (forms[i].querySelector('[name="locale_code"]')) return forms[i];
    }
    return document.querySelector('form[action*="/localization"]');
  }

  /** Formulaire localization dont le bouton langue est actif (hidden inputs / CSRF cohérents). */
  function pmGetLocalizationFormForPost() {
    const activeBtn = document.querySelector(
      'form.shopify-localization-form button[name="locale_code"][aria-selected="true"]'
    );
    const f = activeBtn?.closest?.('form');
    if (f?.querySelector?.('[name="locale_code"]')) return f;
    return pmFindLanguageLocalizationForm();
  }

  /** Tous les boutons langue (header, drawer, footer…) pour mapper le code exact attendu par Shopify. */
  function pmFindAllLocaleCodeButtons() {
    const list = [];
    document.querySelectorAll('form.shopify-localization-form').forEach((f) => {
      if (!f.querySelector('[name="locale_code"]')) return;
      f.querySelectorAll('button[name="locale_code"]').forEach((b) => list.push(b));
    });
    return list;
  }

  function pmSegmentLooksLikeLocaleCode(seg) {
    if (!seg || typeof seg !== 'string') return false;
    return /^[a-z]{2}(-[a-z]{2})?$/i.test(seg.trim());
  }

  function pmLocaleFromShopifyRoutesRoot() {
    const root = window.Shopify?.routes?.root;
    if (!root || typeof root !== 'string' || root === '/') return '';
    const trimmed = root.replace(/\/+$/, '').replace(/^\//, '');
    if (!trimmed) return '';
    const seg = trimmed.split('/')[0];
    return pmSegmentLooksLikeLocaleCode(seg) ? seg.toLowerCase() : '';
  }

  function pmLocaleFromWindowPathname() {
    const path = window.location.pathname || '';
    const parts = path.split('/').filter(Boolean);
    if (!parts.length) return '';
    const seg = parts[0];
    return pmSegmentLooksLikeLocaleCode(seg) ? seg.toLowerCase() : '';
  }

  function pmLocaleFromLocalizationSelection() {
    const sel = document.querySelector(
      'form.shopify-localization-form button[name="locale_code"][aria-selected="true"]'
    );
    return sel?.getAttribute?.('value')?.trim() || '';
  }

  /**
   * Locale pour POST /localization avant checkout.
   * Sans préfixe d’URL (/de/), root + pathname sont souvent vides et html[lang] peut rester sur la langue par défaut :
   * on suit la reco Shopify (Sidekick) — d’abord la valeur Liquid (js-variables), puis le sélecteur actif, puis URL/langue.
   */
  function pmGetCartTargetLocale(form) {
    const hint = typeof PM.storefrontLocale === 'string' ? PM.storefrontLocale.trim() : '';
    if (hint) return hint;

    const fromSelector = pmLocaleFromLocalizationSelection();
    if (fromSelector) return fromSelector;

    const fromSubpath =
      pmLocaleFromShopifyRoutesRoot() ||
      pmLocaleFromWindowPathname() ||
      (document.documentElement.getAttribute('lang') || '').trim().replace(/_/g, '-') ||
      '';
    if (fromSubpath) return fromSubpath;

    const wrap = form?.closest?.('[data-pm-cart-locale]');
    const fromWrap = wrap?.getAttribute?.('data-pm-cart-locale')?.trim();
    if (fromWrap) return fromWrap;
    const fromForm = form?.getAttribute?.('data-pm-locale')?.trim();
    if (fromForm) return fromForm;

    return '';
  }

  /** Token CSRF présent sur les formulaires Shopify (y compris localization). */
  function pmAuthenticityToken() {
    const input = document.querySelector('input[name="authenticity_token"]');
    return input?.value || '';
  }

  /**
   * Codes boutique (de, fr, it) ≠ variantes checkout (de-ch, fr-ch). On aligne sur les value du sélecteur.
   */
  function pmNormalizeLocaleCode(raw) {
    if (!raw || typeof raw !== 'string') return '';
    const s = raw.trim().toLowerCase();
    const short = s.split('-')[0].split('_')[0] || s;
    const btns = pmFindAllLocaleCodeButtons();
    for (let j = 0; j < btns.length; j++) {
      const v = (btns[j].getAttribute('value') || '').trim();
      if (!v) continue;
      const vShort = v.toLowerCase().split('-')[0];
      if (v.toLowerCase() === s || vShort === short) return v;
    }
    return short;
  }

  /** Corps POST /localization (formulaire boutique + pays : préfère l’ancêtre du form panier / tiroir). */
  function pmBuildLocalizationBody(locale, formEl) {
    const code = pmNormalizeLocaleCode(locale);
    if (!code) return null;

    const params = new URLSearchParams();
    const existing = pmGetLocalizationFormForPost();
    if (existing) {
      existing.querySelectorAll('input[type="hidden"]').forEach((input) => {
        if (!input.name) return;
        params.append(input.name, input.value);
      });
    } else {
      params.append('form_type', 'localization');
      params.append('utf8', '\u2713');
      params.append('_method', 'put');
    }

    params.set('locale_code', code);

    const wrap =
      (formEl instanceof HTMLFormElement && formEl.closest?.('[data-pm-cart-locale]')) ||
      document.querySelector('[data-pm-cart-locale]');
    const country = wrap?.getAttribute?.('data-pm-country')?.trim();
    if (country && !params.has('country_code')) params.set('country_code', country);

    const tok = pmAuthenticityToken();
    if (tok && !params.has('authenticity_token')) params.append('authenticity_token', tok);

    params.set('return_to', `${window.location.pathname}${window.location.search || ''}`);
    return { params, code };
  }

  /**
   * POST /localization — sans X-Requested-With (même comportement cookie qu’un vrai formulaire).
   * redirect: manual pour accepter 302/303 où les Set-Cookie sont posés.
   */
  async function pmPostLocalizationFetch(url, params, code) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        Accept: 'text/html,application/xhtml+xml;q=0.9,*/*;q=0.8'
      },
      body: params.toString(),
      credentials: 'include',
      redirect: 'manual'
    });
    const st = res.status;
    const ok = res.ok || st === 302 || st === 303 || st === 307 || st === 308 || st === 204;
    log('localization fetch', st, code, url);
    return ok;
  }

  /**
   * Repli : POST via iframe (navigation réelle) si le fetch ne renvoie pas un succès attendu —
   * certains navigateurs / réglages cookie se comportent mieux ainsi avant le checkout.
   */
  function pmPostLocalizationIframe(url, params) {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      const name = `pm_l10n_${Date.now()}`;
      iframe.setAttribute('name', name);
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:absolute;width:0;height:0;left:-9999px;border:0;visibility:hidden';
      document.body.appendChild(iframe);

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = url;
      form.target = name;
      form.setAttribute('hidden', '');

      params.forEach((value, key) => {
        const inp = document.createElement('input');
        inp.type = 'hidden';
        inp.name = key;
        inp.value = value;
        form.appendChild(inp);
      });
      document.body.appendChild(form);

      let settled = false;
      let maxTimer;
      const finish = (ok) => {
        if (settled) return;
        settled = true;
        if (maxTimer) clearTimeout(maxTimer);
        try {
          form.remove();
        } catch (_) {}
        try {
          iframe.remove();
        } catch (_) {}
        resolve(ok);
      };

      iframe.addEventListener(
        'load',
        () => {
          setTimeout(() => finish(true), 60);
        },
        { once: true }
      );
      iframe.addEventListener(
        'error',
        () => {
          finish(false);
        },
        { once: true }
      );

      form.submit();
      maxTimer = setTimeout(() => finish(true), 10000);
    });
  }

  async function pmPostLocalization(locale, formEl) {
    const built = pmBuildLocalizationBody(locale, formEl);
    if (!built) {
      log('localization skip — pmBuildLocalizationBody null (locale_code vide ?)', locale);
      return false;
    }
    const { params, code } = built;
    const url = `${shopifyRoutesRoot()}localization`;

    try {
      if (await pmPostLocalizationFetch(url, params, code)) return true;
    } catch (err) {
      log('localization fetch error', err);
    }
    log('localization iframe fallback', code);
    return pmPostLocalizationIframe(url, params);
  }

  /**
   * Si l’action du formulaire pointe déjà vers /checkouts/, aligner le segment langue sur la locale cible.
   * Motif courant : …/de/checkouts/… (le regex « locale seulement avant ? ou fin » ne matche pas ce cas).
   */
  function pmPatchCheckoutFormActionLocale(form, rawLocale) {
    const code = pmNormalizeLocaleCode(rawLocale);
    if (!code || !(form instanceof HTMLFormElement)) return;

    const currentAction = form.getAttribute('action') || '';
    if (!currentAction.includes('/checkouts/')) return;

    let next = currentAction.replace(
      /\/([a-z]{2}(?:-[a-z]{2})?)\/checkouts\//i,
      `/${code}/checkouts/`
    );

    if (next === currentAction) {
      next = currentAction.replace(/\/([a-z]{2}(?:-[a-z]{2})?)(\?|$)/i, `/${code}$2`);
    }

    if (next !== currentAction) {
      form.setAttribute('action', next);
      log('checkout form action locale', currentAction, '→', next);
    }
  }

function initCheckoutLocaleSync() {
  once('checkoutLocaleSync', () => {
    const pmCheckoutTermsOk = () => {
      const t = document.getElementById('pm-terms');
      return !t || t.checked;
    };

    const WALLETS_SEL =
      '.additional-checkout-buttons, .shopify-payment-button, .shopify-payment-button__button, .dynamic-checkout, .pm-accelerated-buttons';

    const runCheckoutLocalePipeline = async (form, sub) => {
      const targetLocale = pmGetCartTargetLocale(form);
      log(
        'checkout locale — form action:',
        form.getAttribute('action'),
        '| method:',
        form.getAttribute('method'),
        '| targetLocale:',
        targetLocale
      );
      if (!targetLocale || !window.Shopify?.routes?.root) {
        form.dataset.pmLocalized = 'true';
        sub.click();
        return;
      }
      try {
        await pmPostLocalization(targetLocale, form);
        await new Promise((r) => setTimeout(r, 80));
      } catch (err) {
        log('checkout locale sync', err);
      }
      form.dataset.pmLocalized = 'true';
      sub.click();
    };

    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest?.('button[name="checkout"], input[name="checkout"]');
        if (!(btn instanceof HTMLElement) || btn.disabled) return;
        if (!pmCheckoutTermsOk()) return;
        const form = btn.form;
        if (!(form instanceof HTMLFormElement) || !pmCartCheckoutForm(form)) {
          if (PM.DEBUG && form instanceof HTMLFormElement && btn.getAttribute('name') === 'checkout') {
            log('checkout locale skip (click) — formulaire non reconnu', {
              actionAttr: form.getAttribute('action'),
              actionResolved: form.action,
              className: form.className
            });
          }
          return;
        }
        if (btn.closest(WALLETS_SEL)) return;
        // Flag anti-boucle
        if (form.dataset.pmLocalized === 'true') {
          delete form.dataset.pmLocalized;
          return;
        }
        if (form.dataset.pmL10nBusy === '1') return;

        e.preventDefault();
        e.stopImmediatePropagation();

        form.dataset.pmL10nBusy = '1';
        void (async () => {
          try {
            await runCheckoutLocalePipeline(form, btn);
          } finally {
            delete form.dataset.pmL10nBusy;
          }
        })();
      },
      true
    );

    document.addEventListener(
      'submit',
      async (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!pmCheckoutTermsOk()) return;
        if (!pmCartCheckoutForm(form)) {
          if (PM.DEBUG && form.querySelector('button[name="checkout"], input[name="checkout"]')) {
            log('checkout locale skip (submit) — formulaire non reconnu', {
              actionAttr: form.getAttribute('action'),
              actionResolved: form.action,
              className: form.className
            });
          }
          return;
        }

        let sub = e.submitter;
        if (sub == null) {
          sub = form.querySelector('button[type="submit"], input[type="submit"]');
        }
        if (!(sub instanceof HTMLElement) || !form.contains(sub)) return;
        if (sub.closest(WALLETS_SEL)) return;
        const n = sub.getAttribute('name') || sub.name || '';
        if (n !== 'checkout') return;
        // Flag anti-boucle
        if (form.dataset.pmLocalized === 'true') {
          delete form.dataset.pmLocalized;
          return;
        }
        if (form.dataset.pmL10nBusy === '1') return;

        e.preventDefault();
        e.stopPropagation();

        form.dataset.pmL10nBusy = '1';
        try {
          await runCheckoutLocalePipeline(form, sub);
        } finally {
          delete form.dataset.pmL10nBusy;
        }
      },
      true
    );
  });
}

  function initCartTermsGate() {
    if (!isCart()) return;

    const CHECKBOX_ID = 'pm-terms';
    const CHECKOUT_BTN_SEL =
      'button[name="checkout"], input[name="checkout"], button[type="submit"][name="checkout"], input[type="submit"][name="checkout"]';

    const cb = document.getElementById(CHECKBOX_ID);
    if (!cb) return;

    if (cb.dataset.pmInit === '1') return;
    cb.dataset.pmInit = '1';

    const setLocked = (locked) => {
      document.documentElement.classList.toggle('pm-terms-locked', locked);
      qsa(CHECKOUT_BTN_SEL).forEach((btn) => {
        btn.disabled = !!locked;
        btn.setAttribute('aria-disabled', locked ? 'true' : 'false');
      });
    };

    const showError = () => cb.focus();

    const saveAttribute = async (accepted) => {
      await fetch(`${shopifyRoutesRoot()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { terms_accepted: accepted ? 'yes' : '' } })
      });
    };

    const hydrate = async () => {
      try {
        const r = await fetch(`${shopifyRoutesRoot()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await r.json();
        const accepted = cart?.attributes?.terms_accepted === 'yes';
        cb.checked = !!accepted;
        setLocked(!accepted);
      } catch {
        setLocked(true);
      }
    };

    setLocked(true);
    hydrate();

    cb.addEventListener('change', async () => {
      const accepted = cb.checked;
      setLocked(!accepted);
      try {
        await saveAttribute(accepted);
      } catch {
        cb.checked = false;
        setLocked(true);
        showError();
      }
    });

    once('termsGateClickGuard', () => {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest(CHECKOUT_BTN_SEL);
        if (btn && !cb.checked) {
          e.preventDefault();
          e.stopPropagation();
          showError();
        }
      }, true);
    });

    once('termsGateWalletGuard', () => {
      document.addEventListener(
        'click',
        (e) => {
          if (cb.checked) return;
          const wrap = e.target.closest('.pm-accelerated-buttons');
          if (!wrap) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          showError();
        },
        true
      );
    });

    const cartRoot =
      document.querySelector('.shopify-section--main-cart') ||
      document.querySelector('main#main') ||
      document.body;
    let moTimer;
    const mo = new MutationObserver(() => {
      clearTimeout(moTimer);
      moTimer = setTimeout(() => setLocked(!cb.checked), 120);
    });
    mo.observe(cartRoot, { childList: true, subtree: true });
  }
  /* =====================================================
     MODAL — Délai / Devis
     ===================================================== */

    function initPmOpenButtons() {
    once('pmOpenButtons', () => {
        document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-pm-open]');
        if (!btn) return;

        const type = btn.getAttribute('data-pm-open');

        if (type === 'delay') {
            window.PulsemedOpenDelay && window.PulsemedOpenDelay(btn);
        } else if (type === 'quote') {
            window.PulsemedOpenQuote && window.PulsemedOpenQuote();
        }
        });
    });
    }



  function getModal() { return qs('[data-pm-overlay]'); }

  function initModalForms() {
    const overlay = getModal();
    if (!overlay) return;

    if (overlay.dataset.pmInit === '1') return;
    overlay.dataset.pmInit = '1';

    function debounce(fn, wait = 250) { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; }
    function esc(str){ return String(str).replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[m])); }

    function getI18n(modal) {
      const el = qs('[data-pm-i18n]', modal);
      return {
        needOneProduct: el?.dataset.needOneProduct || 'Please add at least one product.',
        noResults: el?.dataset.noResults || 'No results found.',
        viewProduct: el?.dataset.viewProduct || 'View product',
        valRequired: el?.dataset.valRequired || 'Please fill out this field.',
        valEmail: el?.dataset.valEmail || 'Please enter a valid email address.',
        subjectDelay: el?.dataset.subjectDelay || 'Delivery lead time request',
        subjectQuote: el?.dataset.subjectQuote || 'Quote request'
      };
    }

    function currentType(modal) { return modal.dataset.pmType === 'quote' ? 'quote' : 'delay'; }

    function applyModeVisibility(modal) {
      const type = currentType(modal);
      qsa('[data-pm-mode]', modal).forEach(el => { el.hidden = (el.dataset.pmMode !== type); });
    }

    function applyModeRequired(modal) {
      const type = currentType(modal);
      const quoteNeed = qs('[data-pm-quote-need]', modal);
      const delayMsg = qs('[data-pm-message]', modal);

      if (quoteNeed) { quoteNeed.required = (type === 'quote'); quoteNeed.setCustomValidity(''); }
      if (delayMsg) { delayMsg.required = (type === 'delay'); delayMsg.setCustomValidity(''); }
    }

    async function predictiveSearch(query) {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
      const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return null;
      return res.json();
    }

    function lineHtml(data) {
      const img = data.image ? `<img src="${esc(data.image)}" alt="">` : '';
      return `
        <div class="ddl__row" data-pm-line
             data-title="${esc(data.title || '')}"
             data-variant-title="${esc(data.variantTitle || '')}"
             data-url="${esc(data.url || '')}"
             data-sku="${esc(data.sku || '')}"
             data-variant-id="${esc(String(data.variantId || ''))}"
             data-image="${esc(data.image || '')}">
          <div class="ddl__prod">
            <div class="ddl__thumb">${img}</div>
            <div class="ddl__prodTxt">
              <div class="ddl__prodTitle">${esc(data.title || '')}</div>
              ${data.variantTitle ? `<div class="ddl__prodVariant">${esc(data.variantTitle)}</div>` : ''}
            </div>
          </div>
          <div class="ddl__qty">
            <input type="number" min="1" step="1" value="${data.qty || 1}" class="ddl__qtyInput" aria-label="Qty">
            <button type="button" class="ddl__iconBtn" data-pm-remove aria-label="Remove">🗑</button>
          </div>
        </div>
      `;
    }

    function getLines(container) {
      if (!container) return [];
      return qsa('[data-pm-line]', container).map(row => {
        const qty = parseInt(qs('.ddl__qtyInput', row)?.value || '1', 10);
        return {
          title: row.dataset.title || '',
          variantTitle: row.dataset.variantTitle || '',
          url: row.dataset.url || '',
          sku: row.dataset.sku || '',
          variantId: row.dataset.variantId || '',
          image: row.dataset.image || '',
          qty: Number.isFinite(qty) && qty > 0 ? qty : 1
        };
      });
    }

    function setActiveStep(modal, step) {
      qsa('[data-pm-panel]', modal).forEach(p => p.classList.toggle('is-active', p.dataset.pmPanel === String(step)));
      qsa('[data-pm-step-pill]', modal).forEach(p => p.classList.toggle('is-active', p.dataset.pmStepPill === String(step)));

      const nextBtn = qs('[data-pm-next]', modal);
      const submitBtn = qs('[data-pm-submit]', modal);
      if (nextBtn) nextBtn.hidden = (step === 3);
      if (submitBtn) submitBtn.hidden = (step !== 3);

      modal.dataset.pmStep = String(step);
    }

    function renderReview(modal, I18N) {
      const type = currentType(modal);

      const reviewType = qs('[data-pm-review-type]', modal);
      if (reviewType) reviewType.textContent = (type === 'quote') ? 'Devis' : 'Délai de livraison';

      const reviewLines = qs('[data-pm-review-lines]', modal);
      const reviewContact = qs('[data-pm-review-contact]', modal);
      const reviewMsg = qs('[data-pm-review-message]', modal);

      const lines = getLines(qs('[data-pm-lines]', modal));

      if (reviewLines) {
        reviewLines.innerHTML = lines.length
          ? lines.map(l => `
              <div class="ddl__reviewLinesItem">
                <div class="ddl__reviewThumb">${l.image ? `<img src="${esc(l.image)}" alt="">` : ''}</div>
                <div>
                  <div><strong>${esc(l.title)}</strong></div>
                  <div class="ddl__reviewMeta">
                    ${l.variantTitle ? `${esc(l.variantTitle)} — ` : ''}Qté: <strong>${l.qty}</strong>
                    ${l.sku ? ` — SKU: ${esc(l.sku)}` : ''}
                  </div>
                  ${l.url ? `<div class="ddl__reviewMeta"><a href="${esc(l.url)}" target="_blank" rel="noopener">${esc(I18N.viewProduct)}</a></div>` : ''}
                </div>
              </div>
            `).join('')
          : `<div style="opacity:.7;">—</div>`;
      }

      const form = qs('form.ddl__form', modal);
      const name = form?.querySelector('[name="contact[name]"]')?.value || '';
      const email = form?.querySelector('[name="contact[email]"]')?.value || '';
      const tel = form?.querySelector('[name="contact[telephone]"]')?.value || '';
      const company = form?.querySelector('[name="contact[Société]"]')?.value || '';

      const msgDelay = qs('[data-pm-message]', modal)?.value || '';
      const quoteNeed = qs('[data-pm-quote-need]', modal)?.value || '';
      const message = (type === 'quote') ? quoteNeed : msgDelay;

      if (reviewContact) {
        reviewContact.innerHTML = `
          <div><strong>${esc(name)}</strong></div>
          <div>${esc(email)} ${tel ? `— ${esc(tel)}` : ''}</div>
          ${company ? `<div>${esc(company)}</div>` : ''}
        `;
      }

      if (reviewMsg) {
        reviewMsg.innerHTML = message
          ? `<div class="ddl__reviewMessageBox">${esc(message)}</div>`
          : `<div style="opacity:.7;">—</div>`;
      }
    }

    /** Base absolue page panier : data-pm-cart-permalink-base = shop.url + routes.cart_url */
    function getCartPermalinkBase(modal) {
      const node = modal?.closest?.('[data-pm-cart-permalink-base]') || qs('[data-pm-cart-permalink-base]');
      const raw = node?.getAttribute?.('data-pm-cart-permalink-base') || '';
      if (raw && /^https?:\/\//i.test(raw)) return raw.replace(/\/$/, '');
      const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
      if (!origin) return '';
      const path = getShopifyCartPagePath();
      return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
    }

    /** Segment permalink panier Shopify : VARIANT_ID:QTY */
    function cartPermalinkSegment(variantId, qty) {
      const id = String(variantId || '').replace(/\D/g, '');
      if (!id) return '';
      const q = Math.max(1, parseInt(String(qty), 10) || 1);
      return `${id}:${q}`;
    }

    /** Origine + URL absolue page panier pour return_to */
    function parseCartBaseUrl(cartBase) {
      try {
        const u = new URL(cartBase);
        const cartPath = u.pathname.replace(/\/$/, '') || '/cart';
        const cartPageAbsolute = `${u.origin}${cartPath}`;
        return { origin: u.origin, cartPath, cartPageAbsolute };
      } catch {
        return { origin: '', cartPath: '/cart', cartPageAbsolute: '' };
      }
    }

    /**
     * Ajout au panier puis retour sur la page panier (permalink /cart/V:Q peut envoyer vers le checkout).
     * GET /cart/add?id=VARIANT&quantity=N&return_to=https://…/cart
     */
    function buildCartAddWithReturnUrl(cartBase, variantId, qty) {
      const id = String(variantId || '').replace(/\D/g, '');
      if (!id || !cartBase) return '';
      const q = Math.max(1, parseInt(String(qty), 10) || 1);
      const { origin, cartPageAbsolute } = parseCartBaseUrl(cartBase);
      if (!origin) {
        return `${cartBase.replace(/\/$/, '')}/${cartPermalinkSegment(variantId, qty)}`;
      }
      const params = new URLSearchParams({
        id,
        quantity: String(q),
        return_to: cartPageAbsolute || `${origin}${getShopifyCartPagePath()}`
      });
      let addPath = '/cart/add';
      try {
        const u = new URL(cartPageAbsolute || `${origin}${getShopifyCartPagePath()}`);
        const prefix = u.pathname.replace(/\/cart\/?$/, '');
        addPath = `${prefix}/cart/add`;
      } catch { /* noop */ }
      return `${origin}${addPath}?${params.toString()}`;
    }

    /** Permalink multi-lignes : id1:q1,id2:q2 (secours si plusieurs articles) */
    function buildCartPermalinkUrl(cartBase, lines) {
      if (!cartBase || !lines || !lines.length) return '';
      const parts = lines.map(l => cartPermalinkSegment(l.variantId, l.qty)).filter(Boolean);
      if (!parts.length) return '';
      return `${cartBase.replace(/\/$/, '')}/${parts.join(',')}`;
    }

    function buildEmailBody(modal) {
      const type = currentType(modal);

      const form = qs('form.ddl__form', modal);
      const lines = getLines(qs('[data-pm-lines]', modal));

      const name = form?.querySelector('[name="contact[name]"]')?.value || '';
      const email = form?.querySelector('[name="contact[email]"]')?.value || '';
      const tel = form?.querySelector('[name="contact[telephone]"]')?.value || '';
      const company = form?.querySelector('[name="contact[Société]"]')?.value || '';

      const msgDelay = qs('[data-pm-message]', modal)?.value || '';
      const quoteNeed = qs('[data-pm-quote-need]', modal)?.value || '';
      const message = (type === 'quote') ? quoteNeed : msgDelay;

      const cartBase = getCartPermalinkBase(modal);
      const combinedPermalink = buildCartPermalinkUrl(cartBase, lines);

      const linesText = lines.map(l => {
        const vid = String(l.variantId || '').replace(/\D/g, '');
        const addUrl = vid && cartBase ? buildCartAddWithReturnUrl(cartBase, l.variantId, l.qty) : '';
        const parts = [
          `- ${l.title}${l.variantTitle ? ` / ${l.variantTitle}` : ''}`,
          `  Qté: ${l.qty}`,
          l.sku ? `  SKU: ${l.sku}` : '',
          l.url ? `  URL produit: ${l.url}` : '',
          vid ? `  Variant ID (Shopify): ${vid}` : '',
          addUrl ? `  Lien panier (réponse client): ${addUrl}` : ''
        ].filter(Boolean);
        return parts.join('\n');
      }).join('\n\n');

      const header = (type === 'quote') ? 'DEMANDE DE DEVIS' : 'DEMANDE DE DÉLAI DE LIVRAISON';

      /** Date/heure locale : l’e-mail admin Shopify n’a souvent pas de modèle éditable (bug %{creation_date}) */
      const dateDemande = new Date().toLocaleString('fr-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      return [
        header,
        '',
        `Demande envoyée le : ${dateDemande}`,
        '',
        'Produits:',
        linesText || '- (aucun)',
        (lines.length > 1 && combinedPermalink)
          ? `Lien panier multi (permalink, secours):\n${combinedPermalink}`
          : '',
        '',
        (type === 'quote') ? 'Besoin:' : 'Message:',
        message || '-',
        '',
        'Contact:',
        `Nom: ${name}`,
        `Email: ${email}`,
        tel ? `Téléphone: ${tel}` : null,
        company ? `Société: ${company}` : null
      ].filter(Boolean).join('\n');
    }

    function setupPicker(modal, opts, I18N) {
      const openBtn = qs(opts.openBtnSel, modal);
      const picker = qs(opts.pickerSel, modal);
      const searchInput = qs(opts.searchSel, modal);
      const results = qs(opts.resultsSel, modal);
      const linesWrap = qs(opts.linesSel, modal);
      if (!openBtn || !picker || !searchInput || !results || !linesWrap) return;

      openBtn.addEventListener('click', () => {
        picker.hidden = !picker.hidden;
        if (!picker.hidden) {
          searchInput.value = '';
          results.innerHTML = '';
          searchInput.focus();
        }
      });

      const doSearch = debounce(async () => {
        const q = (searchInput.value || '').trim();
        if (q.length < 2) { results.innerHTML = ''; return; }

        const data = await predictiveSearch(q);
        const products = data?.resources?.results?.products || [];
        const items = [];

        products.forEach(p => {
          const img = p.image || '';
          const url = p.url || '';
          const title = p.title || '';

          items.push({ title, variantTitle: '', sku: '', variantId: p.id || '', url, image: img });

          if (p.variants && p.variants.length) {
            p.variants.slice(0, 5).forEach(v => {
              items.push({
                title,
                variantTitle: v.title || '',
                sku: v.sku || '',
                variantId: v.id || '',
                url,
                image: img
              });
            });
          }
        });

        const seen = new Set();
        const unique = items.filter(it => {
          const id = String(it.variantId || '');
          if (!id) return true;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });

        results.innerHTML = unique.length ? unique.map(item => {
          const meta = [item.variantTitle, item.sku ? `SKU: ${item.sku}` : ''].filter(Boolean).join(' — ');
          const img = item.image ? `<img src="${esc(item.image)}" alt="">` : '';
          return `
            <div class="ddl__pickItem" data-pm-pick
                 data-title="${esc(item.title)}"
                 data-variant-title="${esc(item.variantTitle)}"
                 data-url="${esc(item.url)}"
                 data-sku="${esc(item.sku)}"
                 data-variant-id="${esc(String(item.variantId))}"
                 data-image="${esc(item.image)}">
              <div class="ddl__pickThumb">${img}</div>
              <div>
                <div class="ddl__pickTitle">${esc(item.title)}</div>
                <div class="ddl__pickMeta">${esc(meta)}</div>
              </div>
            </div>
          `;
        }).join('') : `<div style="padding:10px 12px;opacity:.7;">${esc(I18N.noResults)}</div>`;
      }, 250);

      searchInput.addEventListener('input', doSearch);

      results.addEventListener('click', (e) => {
        const pick = e.target.closest('[data-pm-pick]');
        if (!pick) return;

        const vid = String(pick.dataset.variantId || '');
        if (vid && qsa('[data-pm-line]', linesWrap).some(r => r.dataset.variantId === vid)) {
          picker.hidden = true;
          results.innerHTML = '';
          return;
        }

        linesWrap.insertAdjacentHTML('beforeend', lineHtml({
          title: pick.dataset.title,
          variantTitle: pick.dataset.variantTitle,
          url: pick.dataset.url,
          sku: pick.dataset.sku,
          variantId: pick.dataset.variantId,
          image: pick.dataset.image,
          qty: 1
        }));

        picker.hidden = true;
        results.innerHTML = '';
      });
    }

    function openForm(type, triggerEl) {
      const overlay = getModal();
      if (!overlay) return;

      overlay.dataset.pmType = (type === 'quote') ? 'quote' : 'delay';

      const successNotice = overlay.querySelector('[data-pm-success]');
      if (successNotice) successNotice.style.display = 'none';

      overlay.hidden = false;
      overlay.removeAttribute('hidden');
      document.documentElement.style.overflow = 'hidden';

      const isQuote = (overlay.dataset.pmType === 'quote');
      qs('[data-pm-title-delay]', overlay)?.toggleAttribute('hidden', isQuote);
      qs('[data-pm-title-quote]', overlay)?.toggleAttribute('hidden', !isQuote);

      applyModeVisibility(overlay);
      applyModeRequired(overlay);

      const i18n = getI18n(overlay);
      const subject = qs('[data-pm-subject]', overlay);
      const typeField = qs('[data-pm-type]', overlay);
      if (subject) subject.value = isQuote ? i18n.subjectQuote : i18n.subjectDelay;
      if (typeField) typeField.value = isQuote ? 'Devis' : 'Délai';

      setActiveStep(overlay, 1);

      if (!isQuote) {
        const linesWrap = qs('[data-pm-lines]', overlay);
        if (linesWrap && triggerEl?.dataset?.pmVariantId) {
          linesWrap.innerHTML = lineHtml({
            title: triggerEl.dataset.pmTitle || '',
            variantTitle: triggerEl.dataset.pmVariantTitle || '',
            url: triggerEl.dataset.pmUrl || '',
            sku: triggerEl.dataset.pmSku || '',
            variantId: triggerEl.dataset.pmVariantId || '',
            image: triggerEl.dataset.pmImage || '',
            qty: 1
          });
        }
      }
    }

    // API publique (les snippets peuvent appeler ces fonctions)
    window.PulsemedOpenDelay = (triggerEl) => openForm('delay', triggerEl);
    window.PulsemedOpenQuote = () => openForm('quote', null);

    window.PulsemedCloseForm = () => {
      const overlay = getModal();
      if (!overlay) return;
      overlay.hidden = true;
      document.documentElement.style.overflow = '';
    };

    const I18N = getI18n(overlay);

    overlay.addEventListener('click', (e) => { if (e.target === overlay) window.PulsemedCloseForm(); });
    qs('[data-pm-close]', overlay)?.addEventListener('click', window.PulsemedCloseForm);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !overlay.hidden) window.PulsemedCloseForm(); });

    overlay.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pm-remove]');
      if (!btn) return;
      btn.closest('[data-pm-line]')?.remove();
    });

    setupPicker(overlay, {
      openBtnSel: '[data-pm-add-products]',
      pickerSel: '[data-pm-picker]',
      searchSel: '[data-pm-search]',
      resultsSel: '[data-pm-results]',
      linesSel: '[data-pm-lines]'
    }, I18N);

    const formEl = qs('form.ddl__form', overlay);

    if (formEl) {
      const fields = [
        formEl.querySelector('[name="contact[name]"]'),
        formEl.querySelector('[name="contact[email]"]'),
        qs('[data-pm-message]', overlay),
        qs('[data-pm-quote-need]', overlay)
      ].filter(Boolean);

      fields.forEach((field) => {
        field.addEventListener('invalid', () => {
          const isEmail = field.getAttribute('type') === 'email';
          if (isEmail && field.value && !field.checkValidity()) {
            field.setCustomValidity(I18N.valEmail);
          } else if (!String(field.value || '').trim()) {
            field.setCustomValidity(I18N.valRequired);
          } else {
            field.setCustomValidity('');
          }
        }, true);

        field.addEventListener('input', () => field.setCustomValidity(''));
        field.addEventListener('change', () => field.setCustomValidity(''));
      });

      const applyPulsemedEmailBody = () => {
        let text = '';
        try {
          text = buildEmailBody(overlay);
        } catch (err) {
          log('buildEmailBody error', err);
          text = '[Erreur génération du récapitulatif. Contacter le support technique.]';
        }
        const bodyField = qs('[data-pm-body]', overlay);
        if (bodyField) bodyField.value = text;
      };

      /* capture: true — remplir body AVANT tout autre handler / sérialisation */
      formEl.addEventListener('submit', applyPulsemedEmailBody, true);
    }

    qs('[data-pm-prev]', overlay)?.addEventListener('click', () => {
      const step = parseInt(overlay.dataset.pmStep || '1', 10);
      if (step === 1) { window.PulsemedCloseForm(); return; }
      setActiveStep(overlay, Math.max(1, step - 1));
    });

    qs('[data-pm-next]', overlay)?.addEventListener('click', () => {
      const step = parseInt(overlay.dataset.pmStep || '1', 10);
      const type = currentType(overlay);

      if (step === 1) {
        if (type === 'delay') {
          const lines = getLines(qs('[data-pm-lines]', overlay));
          if (!lines.length) { alert(I18N.needOneProduct); return; }
        } else {
          const need = qs('[data-pm-quote-need]', overlay);
          if (need && !need.checkValidity()) { need.reportValidity(); return; }
        }
      }

      if (step === 2 && formEl) {
        const name = formEl.querySelector('[name="contact[name]"]');
        const email = formEl.querySelector('[name="contact[email]"]');
        if (name && !name.checkValidity()) { name.reportValidity(); return; }
        if (email && !email.checkValidity()) { email.reportValidity(); return; }

        if (type === 'delay') {
          const msg = qs('[data-pm-message]', overlay);
          if (msg && !msg.checkValidity()) { msg.reportValidity(); return; }
        } else {
          const need = qs('[data-pm-quote-need]', overlay);
          if (need && !need.checkValidity()) { need.reportValidity(); return; }
        }

        renderReview(overlay, I18N);
      }

      setActiveStep(overlay, Math.min(3, step + 1));
      if (parseInt(overlay.dataset.pmStep || '1', 10) === 3) renderReview(overlay, I18N);
    });

    const autoshow = overlay.dataset.pmAutoshow;
    if (autoshow === 'success' || autoshow === 'error') {
      overlay.hidden = false;
      overlay.removeAttribute('hidden');
      document.documentElement.style.overflow = 'hidden';

      overlay.dataset.pmType = overlay.dataset.pmType || 'delay';
      applyModeVisibility(overlay);
      applyModeRequired(overlay);

      setActiveStep(overlay, autoshow === 'error' ? 2 : 3);
      renderReview(overlay, I18N);
    }

    // lien #devis (toutes langues)
    once('hashDevis', () => {
      document.addEventListener('click', (e) => {
        const a = e.target.closest('a');
        if (!a) return;
        const href = a.getAttribute('href') || '';
        if (!href.includes('#devis')) return;

        const hash = (href.split('#')[1] || '').trim();
        if (hash !== 'devis') return;

        e.preventDefault();
        window.PulsemedOpenQuote && window.PulsemedOpenQuote();
      });
    });

    log('modal init ok');
  }
  /* =====================================================
     BOOT
     ===================================================== */
  function boot() {
    initCheckoutLocaleSync();

    initTabsCustom();
    applyVariantsOpenFlag();

    initBlockImpactVariantAdd();
    initBundleAddToCart();
    initCartQtyBridge();
    initQuantityLimitsGuard();
    initCustomVariantReload();
    initCartTermsGate();
    initModalForms();

    initPmOpenButtons();

    PM.status = {
        cart: !!document.getElementById('pm-terms'),
        modal: !!document.querySelector('[data-pm-overlay]'),
        locked: document.documentElement.classList.contains('pm-terms-locked')
        };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
